import { useCallback, useMemo } from 'react';
import ConfirmationModal from '../../../ConfirmationModal';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { workspacePropertySelector } from '../../../../redux/selectors/workspace';
import { batch } from 'react-redux';
import { useMutation } from '@apollo/client';
import {
  IWorkspaceDeleteAnalysisMutation,
  IWorkspaceDeleteAnalysisMutationVariables,
} from '../../../../utils/apollo/graphqlSchema';
import { WORKSPACE_DELETE_ANALYSES } from '../../../../utils/apollo/workspaces';
import printGraphqlErrors from '../../../../utils/apollo/printGraphqlErrors';
import { populateWorkspace } from '../../../../redux/slices/workspace';
import { useLoadWorkspaceAnalyses } from '../../../../utils/hooks/workspaces';
import toastMessage from '../../../../utils/toastMessage';
import { closeModal } from '../../../../redux/slices/modal';
import { reportDeleted } from '../../../../utils/analytics/events/report/events';
import { useAnalytics } from '../../../../utils/analytics/useAnalytics';
import {
  workspaceUpdated,
  workspaceViewed,
} from '../../../../utils/analytics/events/workspace/events';
import { analysisSelector } from '../../../../redux/selectors/analyses';
import { assertNotNil } from '../../../../utils/types';

interface IProps {
  analysisId: string;
}

export default ({ analysisId }: IProps) => {
  const dispatch = useAppDispatch();
  const { track } = useAnalytics();

  const workspaceName = useAppSelector((state) =>
    workspacePropertySelector(state, 'name'),
  );
  const workspaceId = useAppSelector((state) =>
    workspacePropertySelector(state, 'id'),
  );
  const analysisMemoizedSelector = useMemo(analysisSelector, []);
  const analysis = useAppSelector((state) =>
    analysisMemoizedSelector(state, analysisId),
  );
  const analysisMetaData = analysis?.metadata;
  assertNotNil(analysisMetaData);

  const handleClose = useCallback(() => {
    dispatch(closeModal());
  }, [dispatch]);

  const loadWorkspaceAnalyses = useLoadWorkspaceAnalyses();

  const onDeleteAnalysis = useCallback(
    ({ wsDeleteAnalysis }: IWorkspaceDeleteAnalysisMutation) => {
      const { errors, entity } = wsDeleteAnalysis;

      if (printGraphqlErrors(errors)) {
        return;
      }

      if (entity) {
        track(reportDeleted(analysisMetaData));
        track(workspaceUpdated(entity));
        track(workspaceViewed(entity));

        batch(() => {
          dispatch(populateWorkspace(entity));

          loadWorkspaceAnalyses(entity.rows);
        });

        toastMessage({
          message: `${analysisMetaData.display} has been removed from this workspace`,
          type: 'info',
        });

        handleClose();
      }
    },
    [track, analysisMetaData, handleClose, dispatch, loadWorkspaceAnalyses],
  );

  const [deleteAnalysisFromWorkspace, { loading }] = useMutation<
    IWorkspaceDeleteAnalysisMutation,
    IWorkspaceDeleteAnalysisMutationVariables
  >(WORKSPACE_DELETE_ANALYSES, {
    variables: {
      input: {
        workspaceId,
        analysisId,
      },
    },
    onCompleted: onDeleteAnalysis,
  });

  return (
    <ConfirmationModal
      open
      onClose={handleClose}
      onSubmit={deleteAnalysisFromWorkspace}
      header={'Delete Confirmation'}
      confirmActionLabel={'Delete'}
      disabled={loading}
      trackContext="workspaces"
      trackConfirmAction="delete report - confirm"
      trackCancelAction="delete report - cancel"
    >
      Do you want to delete <strong>{analysisMetaData.display}</strong> from{' '}
      <strong>{workspaceName}</strong> ? This action cannot be undone.
    </ConfirmationModal>
  );
};
